import {
    collection,
    doc,
    type FirestoreDataConverter,
    getDoc,
    QueryDocumentSnapshot,
    serverTimestamp,
    type SnapshotOptions,
    Timestamp,
} from "firebase/firestore";

import { db } from "@/lib/firebase/client.ts";
import type { User } from "@/models/user";
import { DatabaseRepository } from "@/repositories/database-repository.ts";
import AuthService from "@/services/auth-service.ts";

const userConverter: FirestoreDataConverter<User> = {
    toFirestore(user: User) {
        return { ...user };
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): User {
        const data = snapshot.data(options)!;
        return {
            uid: snapshot.id, // always safe
            ...data,
            createdAt: (data.createdAt as Timestamp)?.toDate(),
            updatedAt: (data.updatedAt as Timestamp)?.toDate(),
        } as User;
    },
};

export class UserRepository extends DatabaseRepository<User> {
    private static instance: UserRepository;
    protected readonly collection = collection(db, "users").withConverter(userConverter);

    private constructor() {
        super();
    }

    public static getInstance(): UserRepository {
        if (!UserRepository.instance) {
            UserRepository.instance = new UserRepository();
        }
        return UserRepository.instance;
    }

    async updatePartial(uid: string, patch: Partial<Omit<User, "uid">>): Promise<void> {
        if (patch.displayName) {
            fetch(`${window.location.origin}/api/users/${uid}/name`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${AuthService.getInstance().idToken}`,
                },
                body: JSON.stringify({ displayName: patch.displayName }),
            })
                .then((res) => res.json())
                .then((data) => {
                    console.log(data.message);
                });
        }

        return super.updatePartial(uid, patch);
    }

    async add(user: User): Promise<void> {
        try {
            await this.upsert({ ...user, createdAt: serverTimestamp() });
            return;
        } catch (err) {
            console.error("Failed to add user:", err);
            throw err;
        }
    }

    async getById(uid: string): Promise<User | null> {
        const ref = doc(this.collection, uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data();
        return {
            uid,
            displayName: data.displayName ?? null,
            email: data.email ?? null,
            phone: data.phone ?? null,
            role: data.role ?? null,
            submittedData: data?.submittedData ?? null,
        } satisfies User;
    }
}
